import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  useAddAdmissionsNoteMutation,
  useDeleteAdmissionsNoteMutation,
} from '../../../../state/domainRouterApi';
import { useAdmissionsApplication, useAdmissionsTimeline } from '../../adapters/liveData';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';
import {
  InternalNotesComposerCard,
  InternalNotesListSection,
  type InternalNoteTag,
  normalizeInternalNoteTag,
} from '../../../shared/internal-notes/InternalNotesShared';
import { formatEventLabel } from '../../utils/formatEventLabel';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const asArray = (value: unknown): Array<Record<string, unknown>> =>
  Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];

const readString = (value: unknown): string => String(value ?? '').trim();

const asUnixSeconds = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNoteDate = (epochSeconds: unknown): string => {
  const value = asUnixSeconds(epochSeconds);
  if (value == null || value <= 0) return '';
  try {
    return new Date(value * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const formatEventTime = (epochSeconds: unknown): string => {
  const value = asUnixSeconds(epochSeconds);
  if (value == null || value <= 0) return '';
  try {
    return new Date(value * 1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

export const NotesTab: React.FC = () => {
  const { applicationId } = useOutletContext<{ applicationId: string }>();
  const applicationQuery = useAdmissionsApplication(applicationId);
  const timelineQuery = useAdmissionsTimeline(applicationId);
  const [addNote, addNoteState] = useAddAdmissionsNoteMutation();
  const [deleteNote, deleteNoteState] = useDeleteAdmissionsNoteMutation();

  const notes = useMemo(() => {
    const application = applicationQuery.data as unknown as Record<string, unknown> | undefined;
    return asArray(application?.notes);
  }, [applicationQuery.data]);

  const timelineEvents = useMemo(() => {
    const items = timelineQuery.data?.items ?? [];
    return [...items].sort((a, b) => {
      const aSec = asUnixSeconds(asRecord(a).occurredAt) ?? 0;
      const bSec = asUnixSeconds(asRecord(b).occurredAt) ?? 0;
      return bSec - aSec;
    });
  }, [timelineQuery.data?.items]);

  const [noteText, setNoteText] = useState('');
  const [noteTag, setNoteTag] = useState<InternalNoteTag>('general');

  const onSaveNote = async () => {
    const content = noteText.trim();
    if (!applicationId || !content) return;
    try {
      await addNote({
        applicationId,
        note: {
          noteType: noteTag,
          content,
          isImportant: false,
        },
      }).unwrap();
      setNoteText('');
      setNoteTag('general');
    } catch {
      // UI-level error is shown below; do not dump technical details.
    }
  };

  const onCopyNote = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      // no-op: copy feedback is non-blocking
    }
  };

  const onDeleteNote = async (noteId: string) => {
    if (!applicationId) return;
    try {
      await deleteNote({ applicationId, noteId }).unwrap();
    } catch {
      // UI-level error is shown below.
    }
  };

  const noteListItems = useMemo(
    () =>
      notes.map((note, index) => {
        const content = readString(note.content);
        const noteId = readString(note.id) || `admissions-note-${index}`;
        return {
          id: noteId,
          authorName: readString(note.authorName) || readString(note.createdBy) || 'Staff',
          createdAtLabel: formatNoteDate(note.createdAt) || 'Recent',
          content,
          tag: normalizeInternalNoteTag(note.noteType ?? note.category ?? note.tag),
          important: Boolean(note.isImportant),
          onCopy: () => onCopyNote(content),
          onDelete: () => onDeleteNote(noteId),
        };
      }),
    [notes],
  );

  return (
    <section data-testid="admissions-detail-notes-tab" className="flex flex-col lg:flex-row h-full overflow-y-auto lg:overflow-hidden bg-background-light">
      {/* Notes Column */}
      <div className="w-full lg:w-1/2 px-0 lg:pr-8 lg:overflow-y-auto lg:border-r border-gray-100 pb-10">
        <div className="max-w-2xl mx-auto pt-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-irongrey">Internal Notes</h2>
          </div>

          <InternalNotesComposerCard
            noteText={noteText}
            onNoteTextChange={setNoteText}
            noteTag={noteTag}
            onNoteTagChange={setNoteTag}
            placeholder="Start typing a private note about this application…"
            currentDateLabel={new Date().toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: '2-digit',
            }).toUpperCase()}
            onSave={onSaveNote}
            isSaving={addNoteState.isLoading}
            isDisabled={addNoteState.isLoading}
          />

          {addNoteState.error ? (
            <p className="mt-3 text-sm font-semibold text-irongrey">Unable to save note right now.</p>
          ) : null}
          {deleteNoteState.error ? (
            <p className="mt-3 text-sm font-semibold text-irongrey">Unable to delete note right now.</p>
          ) : null}

          <InternalNotesListSection notes={noteListItems} emptyMessage="No notes yet." />
        </div>
      </div>

      {/* Timeline Column */}
      <div className="w-full lg:w-1/2 px-0 lg:pl-8 lg:overflow-y-auto pb-24 mt-12 lg:mt-0">
        <div className="max-w-2xl mx-auto pt-4">
          <h2 className="text-xl font-bold text-irongrey mb-8">Activity Timeline</h2>

          {timelineQuery.isLoading ? <p className="text-sm text-irongrey/50">Loading…</p> : null}
          {timelineQuery.error ? (
            <SupportErrorMessage>Unable to load timeline right now.</SupportErrorMessage>
          ) : null}

          <div data-testid="admissions-notes-timeline" className="relative ml-4">
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-200" />
            <div className="space-y-10 relative py-1">
              {timelineEvents.length === 0 && !timelineQuery.isLoading ? (
                <p className="text-sm text-irongrey/40 italic">No activity yet.</p>
              ) : null}

              {timelineEvents.map((evt) => {
                const event = asRecord(evt);
                const id = readString(event.id);
                const occurredAt = asUnixSeconds(event.occurredAt) ?? 0;
                const dateLabel = occurredAt
                  ? new Date(occurredAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '';
                const timeLabel = formatEventTime(occurredAt);
                const title = formatEventLabel(readString(event.eventType));
                const rawSummary = readString(event.summary);
                const description = /[._-]/.test(rawSummary) ? formatEventLabel(rawSummary) : rawSummary;

                return (
                  <div key={id} data-testid="admissions-notes-timeline-event" className="relative pl-10">
                    <div className="absolute left-[-5.5px] top-1.5 w-3 h-3 rounded-full bg-primary ring-4 ring-background-light shadow-sm" />
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">{dateLabel || 'Recent'}</span>
                      </div>
                      <p className="text-sm font-bold text-irongrey leading-tight">{title}</p>
                      <p className="text-sm text-irongrey/60 leading-relaxed">{description}</p>
                      <span className="text-[11px] font-medium text-irongrey/30 uppercase">
                        {timeLabel || ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
